import { useFormik } from "formik";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import MultiSelect from "../../../../components/form/select/MultiSelect";
import PageTitle from "../../../../components/partials/PageTitle";
import { getSingleCustomer } from "../../../../redux/slice/customer/customerSlice";
import {
  deleteOtherCover,
  // getAllActiveSavingsPolicies,
  getSavingsPolicyConfig,
  getSavingsPolicyCover,
  resetOtherCovers,
  resetPolicyStatus,
  resetSavingPolicyConfig,
  resetTempData,
  setTempData,
} from "../../../../redux/slice/policy/savingsPolicy/savingsPolicySlice";
import { categories } from "../../../../utils/data/account_type";
import { removeLeadingZeros } from "../../../../utils/helpers/filter";
import { savingsPolicyApplication } from "../../../../utils/validators/schema";
import { savingsPolicyValidator } from "../../../../utils/validators/validators";
import Select from "./../../../../components/form/select/Select";
import { schedulePolicyRepayment } from "./../../../../utils/helpers/functions";
import { getAllActiveSavingsPolicies } from "./../../../../redux/slice/policy/policySlice";
import RiskCovered from "../../../../components/modals/policy/RiskCovered";
import AddOtherSavingsCover from "../../../../components/modals/policy/AddOtherSavingsCover";
import { BsPencilFill } from "react-icons/bs";
import { ImBin } from "react-icons/im";
import { LoaderTwo } from "./../../../../components/loader/LoaderTwo";
import useFileUpload from "../../../../utils/hooks/useFileUpload";
import { FileUploader } from "react-drag-drop-files";

const SavingsPolicyApplication = () => {
  const fileTypes = ["JPG", "PNG", "PDF", "DOC", "DOCX"];
  const {
    isLoading,
    temp_policy_attach_data,
    isUpdated,
    active_savings_policies,
    covers,
    otherCovers,
    savingsPolicyConfig,
  } = useSelector((state) => state.savingsPolicy);
  const { user, user_permissions } = useSelector((state) => state?.auth?.user);
  const { single_customer } = useSelector((state) => state?.customer);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const repaymentRef = useRef();
  const { state: cus_state } = useLocation();

  const { state, id } = cus_state || {};

  const {
    fileUrl,
    loading: uploadingProposalDoc,
    singleFileUpload,
  } = useFileUpload();
  const [isUploading, setIsUploading] = useState(false);
  const [activePoliciesLoading, setActivePoliciesLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [policyId, setPolicyId] = useState(null);
  const [premiumPayment, setPremiumPayment] = useState(
    temp_policy_attach_data?.premuim_contribution
  );
  const [policyIdError, setPolicyIdError] = useState({
    error: "",
    touched: false,
  });

  const breadcrumb = {
    from: {
      title: "policy",
      link: "/admin/savings-policy/application",
    },
    current: {
      title: "policy application",
      link: null,
    },
  };

  const handleFileChange = (file) => {
    setIsUploading(true);
    singleFileUpload(file);
  };

  const onSubmit = (values) => {
    let policyIdCopy = temp_policy_attach_data?.savings_policy_id
      ? temp_policy_attach_data?.savings_policy_id
      : policyId;

    if (!policyIdCopy) {
      setPolicyIdError({
        error: "Policy is required",
        touched: true,
      });
      return;
    }

    let payload = {
      ...values,
      savings_policy_id: policyIdCopy,
      end_date: endDate,
      covers: otherCovers,
      premuim_contribution:
        premiumPayment || temp_policy_attach_data?.premuim_contribution,
      life_id: covers?.get_life_covers[0]?.id,
      total_sum_assured: totalPremium,
      total_premium: totalPremium,
      customer_id: single_customer?.basic_information?.customer_id,
      customer_age: single_customer?.basic_information?.age,
      staff_id: user?.staff_id,
      office_name:
        state?.basic_information.office_name ||
        single_customer?.basic_information?.office_name,
      customer_name: state
        ? state?.basic_information.first_name +
          " " +
          state?.basic_information.last_name
        : single_customer?.basic_information.first_name +
          " " +
          single_customer?.basic_information.last_name,
      agent_name: state
        ? state?.basic_information?.user_first_name +
          " " +
          state?.basic_information?.user_last_name
        : single_customer?.basic_information?.user_first_name +
          " " +
          single_customer?.basic_information.user_last_name,
      proposal_document: fileUrl || temp_policy_attach_data?.proposal_document,
    };
    dispatch(setTempData(payload));
    navigate(`/admin/savings-policy/application-preview`);
  };

  // form control and validations
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      duration:
        temp_policy_attach_data?.duration ||
        (savingsPolicyConfig
          ? savingsPolicyConfig?.data?.p_gen_info[0]?.minimum_duration
          : ""),
      effective_date: moment(new Date()).format("YYYY-MM-DD"),
      repayment_frequency: temp_policy_attach_data?.repayment_frequency,
      sum_assured: temp_policy_attach_data?.sum_assured || "",
      interest_rate:
        temp_policy_attach_data?.rate || savingsPolicyConfig
          ? savingsPolicyConfig?.data?.rate[0]?.default_rate
          : "",
      premuim_contribution: temp_policy_attach_data?.premuim_contribution,
      other_cover_total: null,
    },
    validationSchema: savingsPolicyApplication,
    validate: (values) =>
      // savingsPolicyValidator(
      //   values,
      //   savingsPolicyConfig?.data?.p_gen_info?.length &&
      //     savingsPolicyConfig?.data?.p_gen_info?.[0]?.minimum_duration,
      //   savingsPolicyConfig?.data?.rate
      // ),
      {
        const minimumDuration =
          savingsPolicyConfig?.data?.p_gen_info?.[0]?.minimum_duration || 0;
        const rates = savingsPolicyConfig?.data?.rate || [];
        return savingsPolicyValidator(values, minimumDuration, rates);
      },

    onSubmit,
  });

  // date manipulation starts here
  const calculateEndDate = () => {
    const effectiveDateObj = moment(values?.effective_date);
    const EndDateObj = effectiveDateObj
      .add(values?.duration, "months")
      .subtract(1, "days"); // Subtract 1 day
    const EndDateFormatted = EndDateObj.format("YYYY-MM-DD");

    setEndDate(EndDateFormatted);
  };

  // calculate total premium
  const totalPremium = useMemo(() => {
    let totalPremium = 0;
    if (otherCovers?.length > 0) {
      let total = otherCovers?.reduce((acc, totalOtherCoverPremium) => {
        let calcVal =
          (parseFloat(totalOtherCoverPremium?.sum_assured) *
            parseFloat(totalOtherCoverPremium?.rate)) /
          100;

        return (acc += calcVal);
      }, 0);

      totalPremium = total;
    }
    setFieldValue("other_cover_total", totalPremium);

    return totalPremium + parseFloat(values?.sum_assured);
  }, [otherCovers, values.sum_assured]);

  useEffect(() => {
    if (id) {
      dispatch(getSingleCustomer({ id, office_id: user.office_id }));
    }
  }, [dispatch, id, user.office_id]);

  useEffect(() => {
    if (values?.effective_date || values?.duration) {
      calculateEndDate();
    }
  }, [values?.effective_date, values?.duration]);

  useEffect(() => {
    const data = schedulePolicyRepayment(
      values?.effective_date,
      endDate,
      values?.repayment_frequency,
      totalPremium,
      values?.duration
    );
    setPremiumPayment(data[0]?.amount);
  }, [
    values?.repayment_frequency,
    values?.sum_assured,
    values?.effective_date,
    totalPremium,
  ]);

  useEffect(() => {
    if (!temp_policy_attach_data) {
      setActivePoliciesLoading(true);
      dispatch(getAllActiveSavingsPolicies({ office_id: user.office_id }));
      dispatch(resetPolicyStatus());
      setActivePoliciesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (policyId) {
      const payload = {
        policy_id: policyId,
        office_id: user?.office_id,
      };
      dispatch(resetSavingPolicyConfig());
      resetForm();
      // repaymentRef.current.clearValue();
      dispatch(getSavingsPolicyCover(payload));
      dispatch(getSavingsPolicyConfig(payload));
      dispatch(resetOtherCovers());
    }
  }, [policyId, isUpdated]);

  return (
    <>
      {/* page title */}
      <PageTitle title="Savings Policy Application" breadcrumb={breadcrumb} />
      {/* end page title */}
      <div className="card">
        <form className="card-body" onSubmit={handleSubmit}>
          <div className="border p-3 rounded-3 my-1 position-relative">
            <h4 className="fw-semibold text-primary-1 bg-white position-absolute profile-title">
              Investment Information
            </h4>

            <div className="col-md-12">
              <div className="mb-3">
                <label htmlFor="policy_id" className="form-label">
                  Select Policy <span className="text-danger">*</span>
                </label>
                <Select
                  onBlur={() => {
                    setPolicyIdError({ ...policyIdError, touched: true });
                    if (!policyId) {
                      setPolicyIdError({
                        error: "Policy is required",
                        touched: true,
                      });
                    }
                  }}
                  name="policy_id"
                  id="policy_id"
                  data={active_savings_policies?.savings_policy?.map(
                    (item) => ({
                      label: item.name,
                      value: item.id,
                    })
                  )}
                  value={policyId || temp_policy_attach_data?.savings_policy_id}
                  onChange={(selectedOption) =>
                    setPolicyId(selectedOption?.value)
                  }
                  isError={!policyId && policyIdError?.touched}
                  isLoading={isLoading || activePoliciesLoading}
                />
                {!policyId && policyIdError?.touched && (
                  <p className="mb-0 text-danger">
                    {policyIdError?.error + "*"}
                  </p>
                )}
              </div>
              {savingsPolicyConfig && !isLoading ? (
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        Duration (in months){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        id="duration"
                        name="duration"
                        className={`form-control form-control-sm no-arrows ${
                          errors?.duration && touched?.duration && "is-invalid"
                        }`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.duration}
                        onInput={(e) => removeLeadingZeros(e.target.id)}
                        // placeholder='Enter Duration'
                      />
                      {errors?.duration && touched?.duration && (
                        <span className="text-danger">{errors?.duration}</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        Effective date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        id="effective_date"
                        name="effective_date"
                        className={`form-control form-control-sm no-arrows ${
                          errors?.effective_date &&
                          touched?.effective_date &&
                          "is-invalid"
                        }`}
                        value={values?.effective_date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors?.effective_date && touched?.effective_date && (
                        <span className="text-danger">
                          {errors?.effective_date}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        End date
                      </label>
                      <input
                        type="date"
                        id="end_date"
                        name="end_date"
                        value={endDate}
                        className={`form-control form-control-sm`}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">
                        Repayment Frequency{" "}
                        <span className="text-danger">*</span>
                      </label>

                      <div>
                        <Select
                          isError={
                            errors?.repayment_frequency &&
                            touched?.repayment_frequency
                          }
                          name="repayment_frequency"
                          ref={repaymentRef}
                          data={categories?.map((item) => ({
                            label: item?.name,
                            value: item?.name,
                          }))}
                          value={values.repayment_frequency}
                          onChange={(selectedOption) =>
                            setFieldValue(
                              "repayment_frequency",
                              selectedOption?.value
                            )
                          }
                          onBlur={() => {
                            handleBlur({
                              target: { name: "repayment_frequency" },
                            });
                          }}
                        />
                        {errors?.repayment_frequency &&
                          touched?.repayment_frequency && (
                            <p className="text-danger mb-0">
                              {errors?.repayment_frequency}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        Sum Assured
                      </label>
                      <input
                        type="number"
                        id="sum_assured"
                        name="sum_assured"
                        value={values?.sum_assured}
                        className={`form-control form-control-sm no-arrows ${
                          errors?.sum_assured &&
                          touched?.sum_assured &&
                          "is-invalid"
                        }`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors?.sum_assured && touched?.sum_assured && (
                        <span className="text-danger">
                          {errors?.sum_assured}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        Interest Rate
                      </label>
                      <input
                        type="number"
                        id="interest_rate"
                        name="interest_rate"
                        value={values?.interest_rate}
                        className={`form-control form-control-sm no-arrows ${
                          errors?.interest_rate &&
                          touched?.interest_rate &&
                          "is-invalid"
                        }`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors?.interest_rate && touched?.interest_rate && (
                        <span className="text-danger">
                          {errors?.interest_rate}
                        </span>
                      )}
                    </div>
                  </div>
                  {(premiumPayment ||
                    temp_policy_attach_data?.premuim_contribution) &&
                  values.sum_assured ? (
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label htmlFor="policy_name" className="form-label">
                          Premium Contribution
                        </label>
                        <input
                          type="number"
                          value={
                            premiumPayment ||
                            temp_policy_attach_data?.premuim_contribution
                          }
                          className={`form-control form-control-sm no-arrows`}
                          readOnly
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label
                        className="form-label d-block"
                        htmlFor="proposal_document"
                      >
                        <span
                          className={`${
                            (fileUrl ||
                              temp_policy_attach_data?.proposal_document) &&
                            !uploadingProposalDoc &&
                            "text-success"
                          }`}
                        >
                          Upload Proposal document
                        </span>
                        {!fileUrl && uploadingProposalDoc && (
                          <span>
                            <i className="mdi mdi-dots-circle mdi-spin "></i>
                          </span>
                        )}
                        {(fileUrl ||
                          temp_policy_attach_data?.proposal_document) &&
                          !uploadingProposalDoc && (
                            <span>
                              <i className="mdi mdi-check text-success fs-5"></i>
                            </span>
                          )}
                      </label>
                      <FileUploader
                        label="Browse Document"
                        children={
                          <div
                            className="borde border-dotted p-1 px-2 text-center cursor-p"
                            style={{ border: "2px dashed grey" }}
                          >
                            <i className="uil uil-cloud-upload fs-4"></i> Upload
                            Document
                            <span className="text-primary-dark mt-0"></span>
                          </div>
                        }
                        handleChange={handleFileChange}
                        onDrop={handleFileChange}
                        name="file"
                        types={fileTypes}
                      />
                      {temp_policy_attach_data?.proposal_document ? (
                        <a
                          href={
                            fileUrl ||
                            temp_policy_attach_data?.proposal_document
                          }
                          target="_blank"
                          className="btn btn-sm btn-primary text-white align-items-center align-content-center justify-content-center ms-2"
                        >
                          <i className="mdi mdi-eye text-white fs-5"></i>
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : isLoading ? (
                <LoaderTwo height={12} />
              ) : null}
            </div>
          </div>
          <div className="border p-3 rounded-3 my-3 position-relative">
            <h4 className="fw-semibold text-primary-1 bg-white position-absolute profile-title">
              Risk Covered Information
            </h4>
            {savingsPolicyConfig && !isLoading ? (
              <p className="text-capitalize">
                <span className="fw-bold">Life Cover Name: </span>{" "}
                {covers ? covers?.get_life_covers[0]?.life_cover_name : ""}
              </p>
            ) : isLoading ? (
              <LoaderTwo height={12} />
            ) : null}

            <button
              className="btn btn-sm btn-primary"
              data-bs-toggle="modal"
              data-bs-target="#other-cover"
              type="button"
              onClick={() => setEditData(null)}
            >
              Add Other Cover
            </button>

            <div className="col-md-12 mb-3 mx- mt-2 table-responsive-sm">
              {otherCovers?.length ? (
                <table className="table table-centered table-striped nowrap w-100 no-footer">
                  <thead>
                    <tr>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "140.8px" }}
                        aria-label="Customer: activate to sort column ascending"
                      >
                        Other Cover
                      </th>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "5.8px" }}
                        aria-label="Customer: activate to sort column ascending"
                      >
                        Rate(%)
                      </th>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "140.8px" }}
                        aria-label="Phone: activate to sort column ascending"
                      >
                        sum Assured
                      </th>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "147.8px" }}
                      >
                        Premium
                      </th>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "257.8px" }}
                        aria-label="Email: activate to sort column ascending"
                      >
                        Detail Description
                      </th>

                      <th
                        className="sorting sorting_asc"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "146.8px" }}
                      ></th>
                    </tr>
                  </thead>

                  <tbody>
                    {otherCovers?.map((item, i) => (
                      <tr key={i}>
                        <td className="text-capitalize">
                          {
                            covers?.get_other_cover?.filter(
                              (p) => p.id === item?.cover_id
                            )[0]?.cover_name
                          }
                        </td>
                        <td className="table-user">{item?.rate}</td>
                        <td className="table-user">{item?.sum_assured}</td>
                        <td className="text-dark">{item?.premium}</td>
                        <td>{item?.desc}</td>

                        <td className="text-center">
                          <div className="d-flex gap-2 align-content-center justify-content-center">
                            <span
                              className="text-primary cursor-p"
                              data-bs-toggle="modal"
                              data-bs-target="#other-cover"
                              onClick={() => setEditData(item)}
                            >
                              <BsPencilFill size={17} />
                            </span>
                            <span
                              className="text-danger cursor-p"
                              onClick={() =>
                                dispatch(deleteOtherCover(item?.cover_id))
                              }
                            >
                              <ImBin size={17} />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          </div>
          <div
            className="border p-3 rounded-3 my-3 position-relative"
            onSubmit={() => {}}
          >
            <h4 className="fw-semibold text-primary-1 bg-white position-absolute profile-title">
              Premium Information
            </h4>

            <div className="col-md-4">
              <div className="mb-3">
                <label htmlFor="policy_name" className="form-label">
                  Total Premium
                </label>
                <input
                  type="text"
                  id="total_premium"
                  name="total_premium"
                  className={`form-control form-control-sm`}
                  value={totalPremium || 0}
                  readOnly
                />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <button type="submit" className="btn btn-primary text-white px-4">
              Next
            </button>
          </div>
        </form>
      </div>

      <AddOtherSavingsCover
        modal_id="other-cover"
        editData={editData}
        setEditData={setEditData}
        selectedCovers={otherCovers}
      />
    </>
  );
};

export default SavingsPolicyApplication;
